import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { DotLottiePlayer } from "@dotlottie/react-player";
import VerificationInput from "react-verification-input";
import classNames from "classnames";
import {
  KamaButton,
  KamaButtonTypes,
  KamaColumn,
  KamaFormGroup,
  KamaIcon,
  KamaInput,
  KamaRow,
} from "@kamatech-ir/kama-react-module";
import tools from "utils/tools";
import http from "utils/http";
import { useAppDispatch, useAppSelector } from "hooks/redux.hook";
import {
  setIsJustedloggedIn,
  setIsLoginAnimationVisible,
  setToken,
} from "redux/slices/app.slice";
import packageJson from "../../../package.json";
import IriSVG from "assets/images/iri.svg";
import RefreshSVG from "assets/images/icons/refresh.svg";
import EyeSVG from "assets/images/icons/eye.svg";
import EyeHiddenSVG from "assets/images/icons/eye-hidden.svg";
import FingerprintLOTTIE from "assets/images/fingerprint.lottie";
import FingerprintJSON from "assets/images/fingerprint.json";
import styles from "./AuthPage.module.scss";

type CaptchaModel = {
  id: string;
  base64Image: string;
};
type AuthModel = {
  username: string;
  password: string;
  captcha: string;
};
type AuthModelError = {
  username: boolean;
  password: boolean;
  captcha: boolean;
};
type ForgotPasswordModel = {
  userName: string;
  cellphone: string;
  captcha: string;
  verifyCode: string;
  password: string;
  retypedPassword: string;
};
type ForgotPasswordError = {
  userName: boolean;
  cellphone: boolean;
  captcha: boolean;
  verifyCode: boolean;
  password: boolean;
  retypedPassword: boolean;
};
type ChangePhoneModel = {
  userName: string;
  cellphone: string;
  otp: string;
};
type PageMode =
  | "login"
  | "sendCode"
  | "verifyCode"
  | "setNewPassword"
  | "otp"
  | "changePhone";

const initialModel: AuthModel = {
  username: "",
  password: "",
  captcha: "",
};
const initialErrors: AuthModelError = {
  username: false,
  password: false,
  captcha: false,
};
const initialForgotPasswordModel: ForgotPasswordModel = {
  userName: "",
  cellphone: "",
  captcha: "",
  verifyCode: "",
  password: "",
  retypedPassword: "",
};
const initialForgotPasswordErrors: ForgotPasswordError = {
  userName: false,
  cellphone: false,
  captcha: false,
  verifyCode: false,
  password: false,
  retypedPassword: false,
};
const initialChangePhoneModel: ChangePhoneModel = {
  userName: "",
  cellphone: "",
  otp: "",
};

const fallbackCountdown = 120; // resend code countdown. [ToDo]: rename variable
const initialCaptchaRefreshCountdown = 120;
let timer: NodeJS.Timer | undefined;

function AuthPage() {
  const dispatch = useAppDispatch();

  const [loginType, setLoginType] = useState<1 | 6 | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [captchaLoading, setCaptchaLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [captcha, setCaptcha] = useState<CaptchaModel | null>(null);
  const [model, setModel] = useState<AuthModel>(initialModel);
  const [errors, setErrors] = useState<AuthModelError>(initialErrors);
  const [forgotPasswordModel, setForgotPasswordModel] =
    useState<ForgotPasswordModel>(initialForgotPasswordModel);
  const [changePhoneModel, setChangePhoneModel] = useState<ChangePhoneModel>(
    initialChangePhoneModel
  );
  const [forgotPasswordErrors, setForgotPasswordErrors] =
    useState<ForgotPasswordError>(initialForgotPasswordErrors);
  const [currentMode, setCurrentMode] = useState<PageMode>("login");
  const [countdown, setCountdown] = useState<number>(fallbackCountdown);
  const [userId, setUserId] = useState<string | null>(null);
  const [verifyCodeHasError, setVerifyCodeHasError] = useState<boolean>(false); // merge these two
  const [verifyCodeSuccess, setVerifyCodeSuccess] = useState<boolean>(false); // merge these two
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false); // temp, refactor and move to package
  const [isSetPasswordVisible, setIsSetPasswordVisible] =
    useState<boolean>(false); // temp, refactor and move to package
  const [isSetRetypedPasswordVisible, setIsSetRetypedPasswordVisible] =
    useState<boolean>(false); // temp, refactor and move to package
  const [captchaRefreshCountdown, setCaptchaRefreshCountdown] =
    useState<number>(3);
  const [isCaptchaRefreshPaused, setIsCaptchaRefreshPaused] =
    useState<boolean>(true);
  const [otp, setOtp] = useState<string>("");
  const [sendOtpResponse, setSendOtpResponse] = useState<any>({});
  const [initialCountdown, setInitialCountdown] =
    useState<number>(fallbackCountdown);
  const [isChangePasswordOtp, setIsChangePasswordOtp] =
    useState<boolean>(false);
  const [isCurrentModeChanging, setIsCurrentModeChanging] =
    useState<boolean>(false);

  const { isLoginAnimationVisible } = useAppSelector((state) => {
    return state.app;
  });

  const handleCurrentModeChange = (value: PageMode) => {
    if (!isCurrentModeChanging) {
      setIsCurrentModeChanging(true);
      setTimeout(() => {
        setCurrentMode(value);
      }, 500);
    }
  };
  const fetchLoginType = async () => {
    try {
      const response = await http.get({
        url: "/api/Authentication/GetLoginType",
      });

      setLoginType(response.data.loginType);
    } catch (error) {
      setLoginType(1); // default fallback to user-pass
    }
  };
  const fetchCaptcha = async (type: 1 | 2) => {
    setCaptchaLoading(true);
    try {
      const response = await http.post({
        url: "/api/Captcha/Generate",
        data: { type },
      });
      setCaptcha(response.data);
      setModel(({ ...prev }) => {
        return {
          ...prev,
          captcha: "",
        };
      });
      setForgotPasswordModel(({ ...prev }) => {
        return {
          ...prev,
          captcha: "",
        };
      });

      // reset captcha refresh countdown
      setIsCaptchaRefreshPaused(true);
      setCaptchaRefreshCountdown(initialCaptchaRefreshCountdown);
    } catch (error) {
      setIsCaptchaRefreshPaused(false);
    } finally {
      setCaptchaLoading(false);
    }
  };
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setModel(({ ...prev }) => {
      return {
        ...prev,
        [event.target.name]: event.target.value,
      };
    });
  };
  const handleChangePhoneInputChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setChangePhoneModel(({ ...prev }) => {
      return {
        ...prev,
        [event.target.name]: event.target.value,
      };
    });
  };
  const handleForgotPasswordInputChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setForgotPasswordModel(({ ...prev }) => {
      return {
        ...prev,
        [event.target.name]: event.target.value,
      };
    });
  };
  const handleVerifyCodeChange = (value: string) => {
    setVerifyCodeHasError(false);
    setForgotPasswordModel(({ ...prev }) => {
      return {
        ...prev,
        verifyCode: value,
      };
    });
  };
  const handleOtpChange = (value: string) => {
    setVerifyCodeHasError(false);
    setOtp(value);
  };
  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();

    switch (currentMode) {
      case "login":
        if (loginType === 1) {
          await login();
        } else if (loginType === 6) {
          await sendOtp();
        }
        break;

      case "otp":
        if (isChangePasswordOtp) {
          await changeCellphone();
        } else {
          await login();
        }
        break;

      case "sendCode":
        await sendCode();
        break;

      case "verifyCode":
        handleCurrentModeChange("sendCode");
        break;

      case "setNewPassword":
        await setNewPassword();
        break;

      case "changePhone":
        await sendChangeCellphoneOtp();
        break;

      default:
        break;
    }
  };
  const changeCellphone = async () => {
    setLoading(true);
    try {
      const response = await http.post({
        url: "/api/otp/ChangeCellphone",
        data: tools.encode({
          userName: changePhoneModel.userName, // rename this to nationalCode
          // password: model.password,
          cellphone: changePhoneModel.cellphone,
          otp: otp,
          // sendType: 3,
        }).Hash,
      });

      if (!response.success) {
        setLoading(false);
        return tools.alert.error(response.message);
      }

      setOtp("");
      tools.alert.success("شماره همراه جدید با موفقیت برای شما ثبت شد.");

      handleCurrentModeChange("login");
    } catch (error: any) {
      tools.alert.error(error?.messages);
    } finally {
      setLoading(false);
    }
  };
  const sendChangeCellphoneOtp = async () => {
    if (!changePhoneModel.userName) {
      return tools.alert.error("لطفا کد ملی را وارد کنید.");
    }
    if (!changePhoneModel.cellphone) {
      return tools.alert.error("لطفا شماره همراه جدید را وارد کنید.");
    }

    setLoading(true);
    try {
      const response = await http.post({
        url: "/api/otp/Send",
        data: tools.encode({
          userName: model.username,
          password: model.password,
          cellphone: changePhoneModel.cellphone,
          sendType: 3,
        }).Hash,
      });

      if (!response.success) {
        setLoading(false);
        return tools.alert.error(response.message);
      }

      tools.alert.success(
        response.data.message ||
          "رمز عبور موقت با موفقیت برای شماره موبایل جدید شما ارسال گردید."
      );

      setSendOtpResponse(response.data);
      setInitialCountdown(response.data?.validTime || 120);

      setCountdown(response.data?.validTime || 120);

      handleCurrentModeChange("otp");
    } catch (error: any) {
      tools.alert.error(error?.messages);
    } finally {
      setLoading(false);
    }
  };
  const sendOtp = async () => {
    // validations
    setErrorMessage("");
    setErrors(initialErrors);
    if (!captcha) {
      return; // wait for captcha to be fetched
    }
    if (!model.username) {
      setErrors({ ...initialErrors, username: true });
      return setErrorMessage("نام کاربری را وارد کنید.");
    }
    if (!model.password) {
      setErrors({ ...initialErrors, password: true });
      return setErrorMessage("رمز عبور را وارد کنید.");
    }
    if (!model.captcha) {
      setErrors({ ...initialErrors, captcha: true });
      return setErrorMessage("حروف امنیتی را وارد کنید.");
    } else if (model.captcha.length !== 4) {
      setErrors({ ...initialErrors, captcha: true });
      return setErrorMessage("حروف امنیتی وارد شده اشتباه است.");
    }

    // send otp
    setLoading(true);
    try {
      const response = await http.post({
        url: "/api/otp/Send",
        data: tools.encode({
          clientId: process.env.REACT_APP_CLIENT_ID,
          grantType: "password",
          userName: model.username,
          password: model.password,
          captcha: model.captcha,
          captchaId: captcha?.id,
          sendType: currentMode === "otp" ? 2 : 1,
        }).Hash,
      });

      tools.alert.success(
        response.data.message ||
          "رمز عبور موقت با موفقیت برای شماره موبایل شما ارسال گردید."
      );

      if (!response.success) {
        if (currentMode !== "otp") {
          fetchCaptcha(1);
        }

        setLoading(false);
        return tools.alert.error(response.message);
      }

      setSendOtpResponse(response.data);
      setInitialCountdown(response.data?.validTime || 120);

      setCountdown(response.data?.validTime || 120);

      // set current mode to otp
      handleCurrentModeChange("otp");
    } catch (error: any) {
      if (currentMode !== "otp") {
        fetchCaptcha(1);
      } else {
        handleCurrentModeChange("login");
      }

      tools.alert.error(error?.messages);
    } finally {
      setLoading(false);
    }
  };
  const login = async () => {
    // validations
    setErrorMessage("");
    setErrors(initialErrors);
    if (!captcha) {
      return; // wait for captcha to be fetched
    }
    if (!model.username) {
      setErrors({ ...initialErrors, username: true });
      return setErrorMessage("نام کاربری را وارد کنید.");
    }
    if (!model.password) {
      setErrors({ ...initialErrors, password: true });
      return setErrorMessage("رمز عبور را وارد کنید.");
    }
    if (!model.captcha) {
      setErrors({ ...initialErrors, captcha: true });
      return setErrorMessage("حروف امنیتی را وارد کنید.");
    } else if (model.captcha.length !== 4) {
      setErrors({ ...initialErrors, captcha: true });
      return setErrorMessage("حروف امنیتی وارد شده اشتباه است.");
    }
    if (loginType === 6 && (!otp || otp.length !== 6)) {
      return tools.alert.error("لطفا رمز عبور موقت را وارد کنید.");
    }

    // get token
    setLoading(true);
    try {
      const response = await http.post({
        url: "/api/Authentication/GetToken",
        data: tools.encode({
          clientId: process.env.REACT_APP_CLIENT_ID,
          grantType: "password",
          userName: model.username,
          password: model.password,
          captcha: model.captcha,
          captchaId: captcha?.id,
          Otp: loginType === 6 ? otp : undefined,
          loginType,
        }).Hash,
      });

      if (!response.success) {
        // fetchCaptcha(1);
        setLoading(false);
        return tools.alert.error(response.message);
      }

      const responseData = JSON.parse(response.data);
      if (responseData.Data?.PasswordExpireDate) {
        tools.alert.error(
          "رمز عبور شما منقضی شده است. لطفا مجددا یک رمز عبور جدید تعریف کنید."
        );
        setUserId(responseData.Data?.UserID);
        setLoading(false);
        return handleCurrentModeChange("sendCode");
      }

      setVerifyCodeSuccess(true);
      dispatch(setIsLoginAnimationVisible(true));
      setTimeout(() => {
        // set tokens
        dispatch(setIsJustedloggedIn(true));
        dispatch(setToken(JSON.parse(response.data)));
      }, 5e2);
    } catch (error: any) {
      if (currentMode === "otp") {
        setVerifyCodeHasError(true);
      }
      tools.alert.error(error?.messages);
      // fetchCaptcha(1);
      setLoading(false);
    }
  };
  const sendCode = async () => {
    // validations
    setErrorMessage("");
    setForgotPasswordErrors(initialForgotPasswordErrors);
    if (!captcha) {
      return; // wait for captcha to be fetched
    }
    if (!forgotPasswordModel.userName) {
      setForgotPasswordErrors({
        ...initialForgotPasswordErrors,
        userName: true,
      });
      return setErrorMessage("کد ملی را وارد کنید.");
    }
    if (!forgotPasswordModel.cellphone) {
      setForgotPasswordErrors({
        ...initialForgotPasswordErrors,
        cellphone: true,
      });
      return setErrorMessage("شماره موبایل را وارد کنید.");
    } else if (!/^0\d{10}$/.test(forgotPasswordModel.cellphone)) {
      setForgotPasswordErrors({
        ...initialForgotPasswordErrors,
        cellphone: true,
      });
      return setErrorMessage("شماره موبایل وارد شده اشتباه است.");
    }
    if (!forgotPasswordModel.captcha) {
      setForgotPasswordErrors({
        ...initialForgotPasswordErrors,
        captcha: true,
      });
      return setErrorMessage("حروف امنیتی را وارد کنید.");
    } else if (forgotPasswordModel.captcha.length !== 4) {
      setForgotPasswordErrors({
        ...initialForgotPasswordErrors,
        captcha: true,
      });
      return setErrorMessage("حروف امنیتی وارد شده اشتباه است.");
    }

    // send security code
    setLoading(true);
    try {
      const response = await http.post({
        url: !userId
          ? "/api/Sso/ForgetPassword"
          : "/api/Sso/SendExpxiredSecurityCode",
        data: !userId
          ? {
              clientId: process.env.REACT_APP_CLIENT_ID,
              grantType: "password",
              userName: forgotPasswordModel.userName,
              cellphone: forgotPasswordModel.cellphone,
              captcha: forgotPasswordModel.captcha,
              captchaId: captcha?.id,
            }
          : {
              model: {
                CellPhone: forgotPasswordModel.cellphone,
                ID: userId,
              },
            },
      });

      if (!response.success) {
        fetchCaptcha(2);
        setLoading(false);
        return tools.alert.error(response.message);
      }

      // change mode
      handleCurrentModeChange("verifyCode");
    } catch (error: any) {
      tools.alert.error(error?.messages);
      fetchCaptcha(2);
    } finally {
      setLoading(false);
    }
  };
  const checkSecurityCode = useCallback(async () => {
    // validations
    setErrorMessage("");
    if (!forgotPasswordModel.verifyCode) {
      return setErrorMessage("کد را وارد کنید.");
    } else if (forgotPasswordModel.verifyCode.length !== 6) {
      return setErrorMessage("کد را به صورت کامل وارد کنید.");
    }

    // verify security code
    setLoading(true);
    try {
      const response = await http.post({
        url: "/api/Sso/CheckSecurityCode",
        data: {
          clientId: process.env.REACT_APP_CLIENT_ID,
          grantType: "password",
          userName: forgotPasswordModel.userName,
          cellphone: forgotPasswordModel.cellphone,
          otp: forgotPasswordModel.verifyCode,
          usageType: 2,
        },
      });

      if (!response.success) {
        return tools.alert.error(response.message);
      }

      setVerifyCodeSuccess(true);
      setTimeout(() => {
        // change mode
        handleCurrentModeChange("setNewPassword");
      }, 5e2);
    } catch (error: any) {
      setVerifyCodeHasError(true);
      setForgotPasswordModel((prev) => {
        return {
          ...prev,
          verifyCode: "",
        };
      });
      tools.alert.error(error?.messages);
    } finally {
      setLoading(false);
    }
  }, [
    forgotPasswordModel.userName,
    forgotPasswordModel.cellphone,
    forgotPasswordModel.verifyCode,
  ]);
  const setNewPassword = async () => {
    // validations
    setErrorMessage("");
    if (!forgotPasswordModel.password) {
      return setErrorMessage("رمز عبور را وارد کنید.");
    } else if (!forgotPasswordModel.retypedPassword) {
      return setErrorMessage("تکرار رمز عبور را وارد کنید.");
    } else if (
      forgotPasswordModel.password !== forgotPasswordModel.retypedPassword
    ) {
      return setErrorMessage("رمز عبور و تکرار رمز عبور یکسان نیستند.");
    }

    setLoading(true);
    try {
      const response = await http.post({
        url: "/api/Sso/SetPasswordWithSecuriyStamp",
        data: {
          clientId: process.env.REACT_APP_CLIENT_ID,
          grantType: "password",
          Username: forgotPasswordModel.userName,
          CellPhone: forgotPasswordModel.cellphone,
          otp: forgotPasswordModel.verifyCode,
          Password: forgotPasswordModel.password,
          UsageType: 2,
        },
      });

      if (!response.success) {
        return tools.alert.error(response.message);
      }

      // login
      setModel({
        ...initialModel,
        username: forgotPasswordModel.userName,
        password: forgotPasswordModel.password,
      });
      handleCurrentModeChange("login");
      tools.alert.success(
        "رمز عبور جدید با موفقیت ثبت گردید. اکنون می توانید با رمز عبور جدید وارد شوید."
      );
    } catch (error: any) {
      tools.alert.error(error?.messages);
    } finally {
      setLoading(false);
    }
  };

  // *** IMPORTANT *** REFACTOR ASAP!
  // set isChangePasswordOtp to True
  useEffect(() => {
    if (currentMode === "changePhone") {
      setIsChangePasswordOtp(true);
    } else if (currentMode !== "otp") {
      setIsChangePasswordOtp(false);
    }
  }, [currentMode]);

  // fetch login type
  useEffect(() => {
    fetchLoginType();
  }, []);

  // initial setup
  useEffect(() => {
    setErrorMessage("");
    setVerifyCodeHasError(false);
    setVerifyCodeSuccess(false);
    switch (currentMode) {
      case "login":
        fetchCaptcha(1);
        break;

      case "sendCode":
        fetchCaptcha(2);
        break;

      case "verifyCode":
        setCountdown(initialCountdown);
        break;

      default:
        break;
    }
  }, [currentMode]);

  // set countdown timer (resend verify code)
  useEffect(() => {
    const countdownInterval = setInterval(() => {
      if (currentMode === "verifyCode" || currentMode === "otp") {
        setCountdown((prev: number) => {
          return prev > 0 ? prev - 1 : 0;
        });
      }
    }, 1e3);

    return () => {
      clearInterval(countdownInterval);
    };
  }, [currentMode]);

  // call check security code on input fill
  useEffect(() => {
    if (
      currentMode === "verifyCode" &&
      forgotPasswordModel.verifyCode?.length === 6
    ) {
      checkSecurityCode();
    }
  }, [forgotPasswordModel.verifyCode, checkSecurityCode, currentMode]);

  // submit login form on otp input fill
  useEffect(() => {
    if (currentMode === "otp" && otp?.length === 6) {
      handleSubmit();
    }
  }, [otp, currentMode]);

  // pause/play captcha refresh countdown on document visibility change
  // useEffect(() => {
  //   const handleRefreshCaptchaState = () => {
  //     setIsCaptchaRefreshPaused(!document.hidden ? false : true);
  //   };

  //   document.addEventListener(
  //     "visibilitychange",
  //     handleRefreshCaptchaState,
  //     false
  //   );

  //   return () => {
  //     document.removeEventListener(
  //       "visibilitychange",
  //       handleRefreshCaptchaState,
  //       false
  //     );
  //   };
  // }, []);

  // refresh captcha logic
  useEffect(() => {
    const handleTick = async () => {
      if (!isCaptchaRefreshPaused && captchaRefreshCountdown > 0) {
        try {
          await fetchCaptcha(1);
        } catch (error) {}

        setCaptchaRefreshCountdown((prev) => {
          return prev - 1;
        });
      }
    };

    if (currentMode === "login") {
      timer = setInterval(handleTick, 1000);
    } else {
      if (timer) {
        clearInterval(timer);
      }
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isCaptchaRefreshPaused, captchaRefreshCountdown, currentMode]);

  // reset isCurrentModeChanging
  useEffect(() => {
    if (isCurrentModeChanging) {
      setTimeout(() => {
        setIsCurrentModeChanging(false);
      }, 1e3);
    }
  }, [isCurrentModeChanging]);

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.isLoginAnimationVisible]: isLoginAnimationVisible,
      })}
    >
      <div className={styles.container}>
        {/* side section (description) */}
        <div className={styles.description}>
          <header>
            <img src={IriSVG} alt="ریاست جمهوری" />
            <div className={styles.description}>
              <div className={styles.title}>ریاست جمهوری</div>
              <div className={styles.subtitle}>
                <a
                  href="https://www.aro.gov.ir/"
                  target="_blank"
                  rel="noreferrer"
                >
                  سازمان اداری و استخدامی کشور
                </a>
              </div>
            </div>
          </header>
          <footer>
            <div className={styles.title}>سیــنا</div>
            <div className={styles.subtitle}>سکوی یکپارچه نظام اداری</div>
          </footer>
        </div>

        {/* form section */}
        <form
          onSubmit={handleSubmit}
          className={classNames({
            [styles.isCurrentModeChanging]: isCurrentModeChanging,
          })}
        >
          {/* heading */}
          <div className={styles.heading}>
            <div className={styles.narration}>
              {currentMode === "login" ? "ورود به سامانه" : ""}

              {currentMode === "otp"
                ? !isChangePasswordOtp
                  ? "ورود به سامانه"
                  : "ثبت شماره همراه جدید"
                : ""}

              {["sendCode", "verifyCode", "setNewPassword"].indexOf(
                currentMode
              ) !== -1
                ? "فراموشی رمز عبور"
                : ""}

              {currentMode === "changePhone" ? "ثبت شماره همراه جدید" : ""}
            </div>
            <div className={styles.title}>
              {currentMode === "login" ? "احراز هویت یکپارچه" : ""}
              {currentMode === "sendCode" ? "مرحله اول" : ""}
              {currentMode === "verifyCode" ? "مرحله دوم" : ""}
              {currentMode === "setNewPassword" ? "مرحله سوم" : ""}
              {currentMode === "otp"
                ? !isChangePasswordOtp
                  ? "رمز عبور موقت"
                  : "مرحله دو - رمز عبور موقت"
                : ""}
              {currentMode === "changePhone" ? "مرحله یک" : ""}
            </div>
          </div>

          {/* form content */}
          <div className={styles.inputs}>
            <KamaFormGroup>
              {currentMode === "login" ? (
                <KamaRow>
                  <KamaColumn col={12}>
                    <KamaInput
                      label="نام کاربری"
                      icon="user"
                      name="username"
                      value={model.username}
                      onChange={handleInputChange}
                      error={errors.username ? "error" : ""}
                      autoFocus
                    />
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <div className={styles.toggleVisible}>
                      <KamaInput
                        label="رمز عبور"
                        icon="lock"
                        type={isPasswordVisible ? "text" : "password"}
                        name="password"
                        value={model.password}
                        onChange={handleInputChange}
                        error={errors.password ? "error" : ""}
                      />
                      <img
                        src={isPasswordVisible ? EyeSVG : EyeHiddenSVG}
                        className={styles.toggleIcon}
                        alt="مشاهده"
                        onClick={() => {
                          setIsPasswordVisible((prev) => {
                            return !prev;
                          });
                        }}
                      />
                    </div>
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <div
                      className={classNames(styles.captchaContainer, {
                        [styles.loading]: captchaLoading,
                      })}
                    >
                      <div
                        className={styles.imageContainer}
                        style={{
                          backgroundImage: captcha?.base64Image
                            ? `url(data:image/png;base64,${captcha.base64Image})`
                            : "initial",
                        }}
                      />

                      <button
                        type="button"
                        className={styles.button}
                        onClick={() => {
                          fetchCaptcha(1);
                        }}
                        tabIndex={-1}
                      >
                        <img src={RefreshSVG} alt="تلاش مجدد" />
                      </button>
                    </div>
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <KamaInput
                      label="حروف امنیتی"
                      icon="fingerprint"
                      name="captcha"
                      type="tel"
                      value={model.captcha}
                      onChange={handleInputChange}
                      maxLength={4}
                      prevent="letter"
                      error={errors.captcha ? "error" : ""}
                    />
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <div
                      className={classNames(styles.errorMessage, {
                        [styles.show]: errorMessage,
                      })}
                    >
                      <KamaIcon icon="warning" />
                      <span>{errorMessage}</span>
                    </div>
                  </KamaColumn>
                </KamaRow>
              ) : null}

              {currentMode === "sendCode" ? (
                <KamaRow>
                  <KamaColumn col={12}>
                    <KamaInput
                      label="کد ملی"
                      icon="user"
                      name="userName"
                      value={forgotPasswordModel.userName}
                      onChange={handleForgotPasswordInputChange}
                      error={forgotPasswordErrors.userName ? "error" : ""}
                      autoFocus
                    />
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <KamaInput
                      label="شماره موبایل"
                      icon="phone"
                      type="tel"
                      name="cellphone"
                      prevent="letter"
                      maxLength={11}
                      value={forgotPasswordModel.cellphone}
                      onChange={handleForgotPasswordInputChange}
                      error={forgotPasswordErrors.cellphone ? "error" : ""}
                    />
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <div
                      className={classNames(styles.captchaContainer, {
                        [styles.loading]: captchaLoading,
                      })}
                    >
                      <div
                        className={styles.imageContainer}
                        style={{
                          backgroundImage: captcha?.base64Image
                            ? `url(data:image/png;base64,${captcha.base64Image})`
                            : "initial",
                        }}
                      />

                      <button
                        type="button"
                        className={styles.button}
                        onClick={() => {
                          fetchCaptcha(2);
                        }}
                        tabIndex={-1}
                      >
                        <img src={RefreshSVG} alt="تلاش مجدد" />
                      </button>
                    </div>
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <KamaInput
                      label="حروف امنیتی"
                      icon="fingerprint"
                      name="captcha"
                      type="tel"
                      value={forgotPasswordModel.captcha}
                      onChange={handleForgotPasswordInputChange}
                      maxLength={4}
                      prevent="letter"
                      error={forgotPasswordErrors.captcha ? "error" : ""}
                    />
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <div
                      className={classNames(styles.errorMessage, {
                        [styles.show]: errorMessage,
                      })}
                    >
                      <KamaIcon icon="warning" />
                      <span>{errorMessage}</span>
                    </div>
                  </KamaColumn>
                </KamaRow>
              ) : null}

              {currentMode === "verifyCode" ? (
                <KamaRow>
                  <KamaColumn col={12}>
                    <div className={styles.verificationWrapper}>
                      <VerificationInput
                        value={forgotPasswordModel.verifyCode}
                        onChange={handleVerifyCodeChange}
                        classNames={{
                          container: classNames(styles.verificationContainer, {
                            [styles.hasError]: verifyCodeHasError,
                            [styles.success]: verifyCodeSuccess,
                          }),
                          character: styles.verificationCharacter,
                          characterSelected: styles.selected,
                          characterFilled: styles.filled,
                        }}
                        inputProps={{ type: "tel" }}
                        placeholder=""
                        autoFocus
                      />
                    </div>
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <div
                      className={classNames(styles.errorMessage, {
                        [styles.show]: true,
                      })}
                    >
                      <KamaIcon icon="warning" />
                      <span>
                        رمز عبور موقت به شماره {forgotPasswordModel.cellphone}{" "}
                        ارسال گردید.
                      </span>
                    </div>
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <div
                      className={classNames(styles.errorMessage, {
                        [styles.show]: errorMessage,
                      })}
                    >
                      <KamaIcon icon="warning" />
                      <span>{errorMessage}</span>
                    </div>
                  </KamaColumn>
                </KamaRow>
              ) : null}

              {currentMode === "setNewPassword" ? (
                <KamaRow>
                  <KamaColumn col={12}>
                    <div className={styles.toggleVisible}>
                      <KamaInput
                        label="رمز عبور"
                        icon="lock"
                        type={isSetPasswordVisible ? "text" : "password"}
                        name="password"
                        value={forgotPasswordModel.password}
                        onChange={handleForgotPasswordInputChange}
                        error={forgotPasswordErrors.password ? "error" : ""}
                      />
                      <img
                        src={isSetPasswordVisible ? EyeSVG : EyeHiddenSVG}
                        className={styles.toggleIcon}
                        alt="مشاهده"
                        onClick={() => {
                          setIsSetPasswordVisible((prev) => {
                            return !prev;
                          });
                        }}
                      />
                    </div>
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <div className={styles.toggleVisible}>
                      <KamaInput
                        label="تکرار رمز عبور"
                        icon="lock-open"
                        type={isSetRetypedPasswordVisible ? "text" : "password"}
                        name="retypedPassword"
                        value={forgotPasswordModel.retypedPassword}
                        onChange={handleForgotPasswordInputChange}
                        error={
                          forgotPasswordErrors.retypedPassword ? "error" : ""
                        }
                      />
                      <img
                        src={
                          isSetRetypedPasswordVisible ? EyeSVG : EyeHiddenSVG
                        }
                        className={styles.toggleIcon}
                        alt="مشاهده"
                        onClick={() => {
                          setIsSetRetypedPasswordVisible((prev) => {
                            return !prev;
                          });
                        }}
                      />
                    </div>
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <div className={styles.passwordPolicy}>
                      <ul>
                        <li>رمز عبور باید حداقل ۸ کاراکتر داشته باشد.</li>
                        <li>
                          رمز عبور باید حاوی کاراکترهای خاص (نمادها مثل: #، @،
                          !، $ و...) باشد.
                        </li>
                        <li>رمز عبور باید حاوی حروف بزرگ باشد.</li>
                        <li>رمز عبور باید حاوی حروف کوچک باشد.</li>
                        <li>رمز عبور باید حاوی اعداد باشد.</li>
                      </ul>
                      <ul>
                        <li>
                          از عباراتی که الگوی خاصی (مانند الگوهای کیبورد، عبارات
                          یا اعداد پشت سرهم) دارند؛ استفاده نکنیم.
                        </li>
                        <li>از نام کاربری به عنوان رمز عبور استفاده نکنیم.</li>
                        <li>
                          از اطلاعات شخصی مانند نام، سال تولد، شماره شناسنامه،
                          نام اعضای خانواده و یا شماره موبایل به عنوان رمز عبور
                          استفاده نکنیم.
                        </li>
                      </ul>
                    </div>
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <div
                      className={classNames(styles.errorMessage, {
                        [styles.show]: errorMessage,
                      })}
                    >
                      <KamaIcon icon="warning" />
                      <span>{errorMessage}</span>
                    </div>
                  </KamaColumn>
                </KamaRow>
              ) : null}

              {currentMode === "otp" ? (
                <KamaRow>
                  <KamaColumn col={12}>
                    <div className={styles.verificationWrapper}>
                      <VerificationInput
                        value={otp}
                        onChange={handleOtpChange}
                        classNames={{
                          container: classNames(styles.verificationContainer, {
                            [styles.hasError]: verifyCodeHasError,
                            [styles.success]: verifyCodeSuccess,
                          }),
                          character: styles.verificationCharacter,
                          characterSelected: styles.selected,
                          characterFilled: styles.filled,
                        }}
                        inputProps={{ type: "tel" }}
                        placeholder=""
                        autoFocus
                      />
                    </div>
                  </KamaColumn>
                  {sendOtpResponse && sendOtpResponse?.cellPhones ? (
                    <KamaColumn col={12}>
                      <div
                        className={classNames(styles.errorMessage, {
                          [styles.show]: true,
                        })}
                      >
                        {/* <KamaIcon icon="warning" /> */}
                        <div style={{ textAlign: "right" }}>
                          <div>
                            رمز عبور موقت به شماره‌
                            {Object.keys(sendOtpResponse?.cellPhones).length > 1
                              ? "های "
                              : " "}
                          </div>
                          {Object.keys(sendOtpResponse?.cellPhones)
                            .filter((cellPhone) => { return sendOtpResponse?.cellPhones[cellPhone] })
                            .map((cellPhone, cellPhoneIndex) => {
                              return <div className={styles.numbers} key={cellPhoneIndex}>{cellPhone} {cellPhoneIndex !== 0 ? 'و' : ''}</div>;
                          })}
                          {Object.values(sendOtpResponse?.cellPhones).some(Boolean) ? <div>در پیام‌رسان بله </div> : null}
                          {Object.values(sendOtpResponse?.cellPhones).some(value => value === false) ? <div>و</div> : null}
                          {Object.keys(sendOtpResponse?.cellPhones)
                            .filter((cellPhone) => { return !sendOtpResponse?.cellPhones[cellPhone] })
                            .map((cellPhone, cellPhoneIndex) => {
                              return <div className={styles.numbers} key={cellPhoneIndex}>{cellPhone} {cellPhoneIndex !== 0 ? 'و' : ''}</div>;
                          })}
                          {Object.values(sendOtpResponse?.cellPhones).some(value => value === false) ? <div>با پیامک </div> : null}
                          <div>ارسال گردید.</div>
                        </div>
                      </div>
                    </KamaColumn>
                  ) : null}
                  {!isChangePasswordOtp ? (
                    <KamaColumn col={12}>
                      <div className={styles.animationWrapper}>
                        <div className={styles.animation}>
                          <DotLottiePlayer
                            src={FingerprintJSON}
                            speed={0.8}
                            autoplay
                            loop
                          />
                        </div>
                      </div>
                      <p>
                        در صورتی که برای شماره همراه به ثبت رسیده، رمز عبور موقت
                        دریافت نمی‌کنید و یا این شماره همراه در حال حاضر متعلق
                        به شما نمی‌باشد، می‌توانید از طریق لینک زیر اقدام به ثبت
                        شماره همراه جدید برای خود نمایید.
                        <span
                          className={styles.link}
                          onClick={() => {
                            handleCurrentModeChange("changePhone");
                          }}
                        >
                          ثبت شماره همراه جدید
                        </span>
                      </p>
                    </KamaColumn>
                  ) : null}
                </KamaRow>
              ) : null}

              {currentMode === "changePhone" ? (
                <KamaRow>
                  <KamaColumn col={12}>
                    <KamaInput
                      label="کد ملی"
                      icon="user"
                      name="userName"
                      value={changePhoneModel.userName}
                      onChange={handleChangePhoneInputChange}
                      autoFocus
                    />
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <KamaInput
                      label="شماره همراه جدید"
                      icon="phone"
                      name="cellphone"
                      type="tel"
                      value={changePhoneModel.cellphone}
                      onChange={handleChangePhoneInputChange}
                    />
                  </KamaColumn>
                  <KamaColumn col={12}>
                    <p style={{ marginTop: "30px" }}>
                      توجه داشته باشید در فرایند ثبت شماره همراه جدید، کد ملی و
                      شماره همراه جدید وارد شده توسط وب سرویس شاهکار سازمان
                      فناوری اطلاعات و ارتباطات (وب سرویس بررسی اصالت مالکیت
                      شماره همراه) مورد بررسی قرار می‌گیرد. در صورتی که اصالت
                      مالکیت شماره همراه مورد تایید این وب سرویس قرار گیرد، رمز
                      عبور موقت جهت ثبت نهایی شماره همراه جدید برای شما ارسال
                      خواهد شد.
                    </p>
                  </KamaColumn>
                </KamaRow>
              ) : null}
            </KamaFormGroup>
          </div>

          {/* buttons */}
          <div className={styles.buttons}>
            {/* primary button */}
            {currentMode === "otp" ? (
              <KamaButton
                as={KamaButtonTypes.BUTTON}
                className={styles.cta}
                disabled={loading || (currentMode === "otp" && countdown !== 0)}
                onClick={() => {
                  if (isChangePasswordOtp) {
                    sendChangeCellphoneOtp();
                  } else {
                    sendOtp();
                  }
                }}
              >
                {loading || !loginType
                  ? "..."
                  : currentMode === "otp"
                  ? countdown !== 0
                    ? tools.formatSecondsToTimeString(countdown)
                    : "دریافت مجدد رمز عبور موقت"
                  : ""}
              </KamaButton>
            ) : currentMode === "changePhone" ? (
              <KamaButton className={styles.cta} disabled={loading}>
                {loading || !loginType
                  ? "..."
                  : currentMode === "changePhone"
                  ? "ادامه"
                  : ""}
              </KamaButton>
            ) : (
              <KamaButton
                className={styles.cta}
                disabled={
                  loading || (currentMode === "verifyCode" && countdown !== 0)
                }
              >
                {loading || !loginType
                  ? "..."
                  : currentMode === "login"
                  ? "ورود"
                  : currentMode === "sendCode"
                  ? "دریافت رمز عبور موقت"
                  : currentMode === "verifyCode"
                  ? countdown !== 0
                    ? tools.formatSecondsToTimeString(countdown)
                    : "دریافت مجدد رمز عبور موقت"
                  : "ثبت نهایی رمز عبور جدید"}
              </KamaButton>
            )}

            {/* secondary button */}
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => {
                currentMode === "login"
                  ? handleCurrentModeChange("sendCode")
                  : handleCurrentModeChange("login");
              }}
            >
              {currentMode === "login"
                ? "رمز عبور را فراموش کردم!"
                : currentMode === "setNewPassword"
                ? "انصراف"
                : "بازگشت"}
            </button>
          </div>
        </form>

        {/* footer */}
        <div className={styles.copyright}>
          <p>
            تمامی حقوق این پلتفرم متعلق به{" "}
            <a href="https://www.aro.gov.ir/" target="_blank" rel="noreferrer">
              سازمان اداری و استخدامی کشور
            </a>{" "}
            می‌باشد. نسخه <span>{packageJson.version}</span>
          </p>
          <p>Single Sign On Technology</p>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
